/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { SiFacebook, SiInstagram, SiX, SiYoutube } from '@icons-pack/react-simple-icons';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

const SOCIAL_LINKS = [
    {
        Icon: SiFacebook,
        href: 'https://facebook.com/stjude',
        ariaLabelKey: 'socialMedia.facebookLabel',
        microLabelKey: 'connect.followLabel',
        bgClassName: 'bg-brand-social-facebook',
        labelClassName: 'text-brand-sky',
    },
    {
        Icon: SiX,
        href: 'https://x.com/stjude',
        ariaLabelKey: 'socialMedia.xLabel',
        microLabelKey: 'connect.followLabel',
        bgClassName: 'bg-brand-social-x',
        labelClassName: 'text-brand-sky',
    },
    {
        Icon: SiInstagram,
        href: 'https://instagram.com/stjude',
        ariaLabelKey: 'socialMedia.instagramLabel',
        microLabelKey: 'connect.followLabel',
        bgClassName: 'bg-brand-social-instagram',
        labelClassName: 'text-brand-sky',
    },
    {
        Icon: SiYoutube,
        href: 'https://youtube.com/user/MyStJude',
        ariaLabelKey: 'socialMedia.youtubeLabel',
        microLabelKey: 'connect.subscribeLabel',
        bgClassName: 'bg-brand-social-youtube',
        labelClassName: 'text-brand-social-youtube',
    },
] as const;

export default function SocialIcons({ className }: { className?: string }): ReactElement {
    const { t } = useTranslation('footer');
    return (
        <div className={cn('flex items-start gap-6', className)}>
            {SOCIAL_LINKS.map(({ Icon, href, ariaLabelKey, microLabelKey, bgClassName, labelClassName }) => (
                <div key={ariaLabelKey} className="flex flex-col items-center gap-2">
                    <a
                        href={href}
                        aria-label={t(ariaLabelKey)}
                        className={cn(
                            'flex items-center justify-center w-12 h-12 rounded-full text-white transition-opacity hover:opacity-90',
                            bgClassName
                        )}>
                        <Icon className="w-5 h-5" />
                    </a>
                    <span className={cn('text-xs font-medium', labelClassName)}>{t(microLabelKey)}</span>
                </div>
            ))}
        </div>
    );
}
